import React, { useState, useEffect, useCallback } from 'react';
import { getConsent, acceptAll, rejectNonEssential, saveConsent } from '../services/CookieConsentService';
import { Shield, Cookie, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

/**
 * P1-001: KVKK/GDPR Cookie Consent Banner
 * 
 * Shows on first visit (no consent stored).
 * Provides: Accept All / Reject Non-Essential / Manage Preferences.
 * Links to Privacy Policy and Cookie Policy.
 * Does NOT block UI — positioned as a fixed bottom banner.
 */
const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for backend-triggered consent invalidation (version change)
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail) {
        // Consent was cleared (version mismatch or explicit clear)
        setVisible(true);
      }
    };
    window.addEventListener('treatx-consent-changed', handler);
    return () => window.removeEventListener('treatx-consent-changed', handler);
  }, []);

  const handleAcceptAll = useCallback(() => {
    acceptAll('banner');
    setVisible(false);
  }, []);

  const handleReject = useCallback(() => {
    rejectNonEssential('banner');
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    saveConsent(preferences, 'banner');
    setVisible(false);
  }, [preferences]);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      // PRE-M-C — full-width overlay wrapper must NOT intercept clicks
      // outside the visible card. The transparent bounding-box was
      // covering [data-testid=send-btn] on /#/clinic/messages.
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-500 pointer-events-none"
    >
      <div className="mx-auto max-w-5xl px-4 pb-4 pointer-events-auto">
        <div className="bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
          {/* Main Banner */}
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-0.5">
                <div className="h-10 w-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-teal-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-white mb-1">
                  Your Privacy Matters
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  We use essential cookies to make TreatX work. With your consent, we may also use analytics cookies to improve our services.
                  You can change your preferences at any time.{' '}
                  <a
                    href="#/privacy-policy"
                    className="text-teal-400 hover:text-teal-300 underline underline-offset-2 inline-flex items-center gap-0.5"
                    data-testid="cookie-privacy-link"
                  >
                    Privacy Policy <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2 mt-4 ml-14">
              <Button
                onClick={handleReject}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-700/50 text-xs h-8 px-4"
                data-testid="cookie-reject-btn"
              >
                Reject Non-Essential
              </Button>
              <Button
                onClick={() => setShowDetails(!showDetails)}
                variant="outline"
                size="sm"
                className="text-slate-200 border-slate-600 hover:bg-slate-700/50 hover:text-white text-xs h-8 px-4"
                data-testid="cookie-manage-btn"
              >
                Manage Preferences
                {showDetails ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
              </Button>
              <Button
                onClick={handleAcceptAll}
                size="sm"
                className="bg-teal-600 hover:bg-teal-500 text-white text-xs h-8 px-6 font-medium"
                data-testid="cookie-accept-btn"
              >
                Accept All
              </Button>
            </div>
          </div>

          {/* Expanded Preferences */}
          {showDetails && (
            <div
              className="border-t border-slate-700/50 p-5 bg-slate-800/50"
              data-testid="cookie-preferences-panel"
            >
              <div className="space-y-3 ml-14">
                {/* Essential — always on */}
                <label className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30">
                  <div>
                    <span className="text-sm font-medium text-white">Essential</span>
                    <p className="text-xs text-slate-400 mt-0.5">Authentication, security, basic functionality. Always required.</p>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    <div className="h-5 w-9 rounded-full bg-teal-500 relative cursor-not-allowed opacity-70">
                      <div className="absolute right-0.5 top-0.5 h-4 w-4 rounded-full bg-white" />
                    </div>
                  </div>
                </label>

                {/* Analytics */}
                <label
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30 cursor-pointer hover:bg-slate-700/50 transition-colors"
                  data-testid="cookie-toggle-analytics"
                >
                  <div>
                    <span className="text-sm font-medium text-white">Analytics</span>
                    <p className="text-xs text-slate-400 mt-0.5">Usage statistics and performance monitoring to improve our services.</p>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    <button
                      role="switch"
                      aria-checked={preferences.analytics}
                      onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                      className={`h-5 w-9 rounded-full relative transition-colors ${preferences.analytics ? 'bg-teal-500' : 'bg-slate-600'}`}
                    >
                      <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${preferences.analytics ? 'right-0.5' : 'left-0.5'}`} />
                    </button>
                  </div>
                </label>

                {/* TREATX-COOKIE-MARKETING-CATEGORY-REMOVAL-01 — TreatX uses
                    no advertising, retargeting or social-media tracking
                    technology, so no Marketing category is offered. The
                    stored consent record keeps `marketing:false` for
                    backward compatibility with historical evidence. */}

                <div className="flex justify-end pt-2">
                  <Button
                    onClick={handleSavePreferences}
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-500 text-white text-xs h-8 px-6"
                    data-testid="cookie-save-preferences-btn"
                  >
                    Save Preferences
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
